import {
  computed,
  DestroyRef,
  effect,
  Injectable,
  inject,
  NgZone,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CallCompletionMetrics } from '../models/call-completion-metrics.model';
import { CallRuntimeMetrics } from '../models/call-runtime-metrics.model';
import {
  CallSessionLifecycleEvent,
  SessionLifecycleEventType,
} from '../models/call-session-lifecycle-event.model';
import { CallStatus } from '../models/call-status.enum';
import { SessionPhase } from '../models/session-phase.enum';
import { ZoneOutsideTicker } from '../../shared/zone-outside-ticker';
import { CallStateStore } from './call-state.store';
import { SipCoreService } from './sip-core.service';

const TICK_INTERVAL_MS = 1000;

@Injectable({ providedIn: 'root' })
export class CallSessionRuntimeService {
  private readonly store = inject(CallStateStore);
  private readonly sipCore = inject(SipCoreService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly zone = inject(NgZone);

  private readonly ticker = new ZoneOutsideTicker(this.zone, {
    intervalMs: TICK_INTERVAL_MS,
    onTick: wholeSeconds => this.applyTick(wholeSeconds),
  });
  private readonly sessionIdState = signal<string | null>(null);
  private readonly directionState = signal<'inbound' | 'outbound' | null>(null);
  private readonly queueNameState = signal<string | null>(null);
  private readonly phaseState = signal<SessionPhase>(SessionPhase.Idle);
  private readonly talkSecondsState = signal(0);
  private readonly holdSecondsState = signal(0);
  private readonly muteSecondsState = signal(0);
  private readonly acwSecondsState = signal(0);

  private callStartedLogged = false;
  private callEndedLogged = false;
  private pendingRejectReason: string | null = null;

  readonly sessionId = computed(() => this.sessionIdState());
  readonly queueName = computed(() => this.queueNameState());
  readonly phase = computed(() => this.phaseState());
  readonly isAfterCallWork = computed(
    () => this.phaseState() === SessionPhase.AfterCallWork
  );

  readonly talkSeconds = computed(() => this.talkSecondsState());
  readonly holdSeconds = computed(() => this.holdSecondsState());
  readonly muteSeconds = computed(() => this.muteSecondsState());
  readonly acwSeconds = computed(() => this.acwSecondsState());

  readonly talkDuration = computed(() =>
    this.formatSeconds(this.talkSecondsState())
  );
  readonly holdDuration = computed(() =>
    this.formatSeconds(this.holdSecondsState())
  );
  readonly muteDuration = computed(() =>
    this.formatSeconds(this.muteSecondsState())
  );
  readonly acwDuration = computed(() =>
    this.formatSeconds(this.acwSecondsState())
  );

  readonly metrics = computed<CallRuntimeMetrics | null>(() => {
    const sessionId = this.sessionIdState();
    if (!sessionId) {
      return null;
    }

    return {
      sessionId,
      phase: this.phaseState(),
      talkSeconds: this.talkSecondsState(),
      holdSeconds: this.holdSecondsState(),
      muteSeconds: this.muteSecondsState(),
      acwSeconds: this.acwSecondsState(),
    };
  });

  constructor() {
    effect(() => this.syncFromStore());
    effect(() => this.syncTickerState());

    this.sipCore.onCallEnded$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => this.onCallEnded(event));

    this.destroyRef.onDestroy(() => {
      this.ticker.stop();
      this.resetRuntimeState();
    });
  }

  startAfterCallWork(
    source: 'local' | 'remote' | 'network' | 'manual' | 'status' = 'manual'
  ): void {
    if (!this.sessionIdState() || this.isAfterCallWork()) {
      return;
    }

    this.logCallEndedIfNeeded(source);
    this.phaseState.set(SessionPhase.AfterCallWork);
    this.logSessionEvent({
      type: 'AcwStarted',
      sessionId: this.sessionIdState()!,
      direction: this.directionState() ?? undefined,
      source,
    });
  }

  completeAfterCallWork(): CallCompletionMetrics | null {
    if (!this.sessionIdState() || !this.isAfterCallWork()) {
      return null;
    }

    const completion = this.buildCompletionMetrics();
    this.completeSession('acw-completed', completion);
    return completion;
  }

  setPendingRejectReason(reason: string): void {
    if (!this.sessionIdState()) {
      return;
    }

    this.pendingRejectReason = reason;
  }

  completeRejectedSession(reason: string): void {
    if (!this.sessionIdState()) {
      return;
    }

    this.pendingRejectReason = reason;
    this.completeSession(reason);
  }

  private syncFromStore(): void {
    const session = this.store.currentSession();
    const status = this.store.callStatus();
    const runtimeSessionId = this.sessionIdState();

    if (session && session.id !== runtimeSessionId) {
      this.beginSession(session.id, session.direction, session.queueName ?? null);
    }

    if (!this.sessionIdState()) {
      this.phaseState.set(SessionPhase.Idle);
      return;
    }

    if (this.isAfterCallWork()) {
      return;
    }

    switch (status) {
      case CallStatus.Trying:
        this.phaseState.set(SessionPhase.Trying);
        break;
      case CallStatus.Ringing:
        this.phaseState.set(SessionPhase.Ringing);
        break;
      case CallStatus.Active:
        this.logCallStartedIfNeeded();
        this.phaseState.set(SessionPhase.Active);
        break;
      case CallStatus.Held:
        this.logCallStartedIfNeeded();
        this.phaseState.set(SessionPhase.Held);
        break;
      case CallStatus.Terminated:
        if (!this.callStartedLogged) {
          this.completeSession(this.pendingRejectReason ?? 'terminated-before-established');
          return;
        }

        if (!this.callEndedLogged) {
          this.startAfterCallWork('status');
        }
        break;
      case CallStatus.Registered:
        if (!session && this.sessionIdState()) {
          if (this.callStartedLogged && !this.callEndedLogged) {
            this.startAfterCallWork('status');
            return;
          }
          this.completeSession(this.pendingRejectReason ?? 'session-ended-registered');
        }
        break;
      case CallStatus.Idle:
        if (!session) {
          if (this.callStartedLogged && !this.callEndedLogged) {
            this.startAfterCallWork('status');
            return;
          }
          this.completeSession(this.pendingRejectReason ?? 'session-idle');
        }
        break;
      default:
        break;
    }
  }

  private syncTickerState(): void {
    const phase = this.phaseState();
    const shouldRun =
      phase === SessionPhase.Active ||
      phase === SessionPhase.Held ||
      phase === SessionPhase.AfterCallWork;

    if (shouldRun) {
      this.ticker.start();
    } else {
      this.ticker.stop();
    }
  }

  private applyTick(wholeSeconds: number): void {
    const phase = this.phaseState();

    if (
      phase !== SessionPhase.AfterCallWork &&
      phase !== SessionPhase.Active &&
      phase !== SessionPhase.Held
    ) {
      return;
    }

    if (phase === SessionPhase.AfterCallWork) {
      this.talkSecondsState.update(v => v + wholeSeconds);
      this.acwSecondsState.update(v => v + wholeSeconds);
      return;
    }

    // A dialed queue answers only to play hold music; the conversation starts when an agent is bridged.
    if (this.store.currentSession()?.awaitingQueueAgent) {
      return;
    }

    this.talkSecondsState.update(v => v + wholeSeconds);

    if (this.store.isHeld()) {
      this.holdSecondsState.update(v => v + wholeSeconds);
    }

    if (this.store.isMuted()) {
      this.muteSecondsState.update(v => v + wholeSeconds);
    }
  }

  private onCallEnded(event: {
    sessionId: string;
    wasEstablished: boolean;
    source: 'local' | 'remote' | 'network';
  }): void {
    if (!this.sessionIdState() || this.sessionIdState() !== event.sessionId) {
      return;
    }

    if (event.wasEstablished || this.callStartedLogged) {
      this.startAfterCallWork(event.source);
      return;
    }

    this.logCallEndedIfNeeded(event.source);
    this.completeSession(this.pendingRejectReason ?? 'rejected-before-established');
  }

  private beginSession(
    sessionId: string,
    direction: 'inbound' | 'outbound',
    queueName: string | null = null
  ): void {
    if (this.sessionIdState() && this.sessionIdState() !== sessionId) {
      this.completeSession('session-replaced');
    }

    this.sessionIdState.set(sessionId);
    this.directionState.set(direction);
    this.queueNameState.set(queueName);
    this.phaseState.set(SessionPhase.Created);
    this.callStartedLogged = false;
    this.callEndedLogged = false;
    this.pendingRejectReason = null;
    this.talkSecondsState.set(0);
    this.holdSecondsState.set(0);
    this.muteSecondsState.set(0);
    this.acwSecondsState.set(0);

    this.logSessionEvent({
      type: 'SessionCreated',
      sessionId,
      direction,
    });
  }

  private logCallStartedIfNeeded(): void {
    if (this.callStartedLogged || !this.sessionIdState()) {
      return;
    }

    this.callStartedLogged = true;
    this.logSessionEvent({
      type: 'CallStarted',
      sessionId: this.sessionIdState()!,
      direction: this.directionState() ?? undefined,
      payload: this.metrics() ? { ...this.metrics()! } : undefined,
    });
  }

  private logCallEndedIfNeeded(
    source: 'local' | 'remote' | 'network' | 'manual' | 'status'
  ): void {
    if (this.callEndedLogged || !this.sessionIdState()) {
      return;
    }

    this.callEndedLogged = true;
    this.phaseState.set(SessionPhase.Ended);
    this.logSessionEvent({
      type: 'CallEnded',
      sessionId: this.sessionIdState()!,
      direction: this.directionState() ?? undefined,
      source,
      payload: this.metrics() ? { ...this.metrics()! } : undefined,
    });
  }

  private completeSession(
    reason: string,
    completion?: CallCompletionMetrics
  ): void {
    const sessionId = this.sessionIdState();
    if (!sessionId) {
      return;
    }

    this.logCallEndedIfNeeded('status');

    const completionMetrics = completion ?? this.buildCompletionMetrics();
    const isRejected = !this.callStartedLogged || reason.includes('rejected');
    this.phaseState.set(isRejected ? SessionPhase.Rejected : SessionPhase.Completed);

    this.logSessionEvent({
      type: 'SessionCompleted',
      sessionId,
      direction: this.directionState() ?? undefined,
      reason,
      payload: {
        ...completionMetrics,
        ...this.metrics(),
      },
    });

    this.resetRuntimeState();
  }

  private buildCompletionMetrics(): CallCompletionMetrics {
    return {
      totalCallDuration: this.talkDuration(),
      holdDuration: this.holdDuration(),
      muteDuration: this.muteDuration(),
      acwDuration: this.acwDuration(),
      acwSeconds: this.acwSecondsState(),
      wrapUpId: null,
      rescheduleAtUtc: null,
    };
  }

  private resetRuntimeState(): void {
    this.ticker.stop();
    this.sessionIdState.set(null);
    this.directionState.set(null);
    this.queueNameState.set(null);
    this.phaseState.set(SessionPhase.Idle);
    this.talkSecondsState.set(0);
    this.holdSecondsState.set(0);
    this.muteSecondsState.set(0);
    this.acwSecondsState.set(0);
    this.callStartedLogged = false;
    this.callEndedLogged = false;
    this.pendingRejectReason = null;
  }

  private logSessionEvent(event: CallSessionLifecycleEvent): void {
    const payload = {
      sessionId: event.sessionId,
      direction: event.direction ?? this.directionState(),
      phase: this.phaseState(),
      reason: event.reason ?? null,
      source: event.source ?? null,
      ...event.payload,
    };

    this.log(event.type, payload);
  }

  private log(type: SessionLifecycleEventType, payload: Record<string, unknown>): void {
    console.log(`[AgentDesktop][Session] ${type}`, payload);
  }

  private formatSeconds(totalSeconds: number): string {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
}
